
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { AlertTriangle, Heart } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const DeleteAccount = () => {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const navigate = useNavigate();
  const session = useSession();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Require confirmation text
    if (confirmText !== "ELIMINAR MI CUENTA") {
      toast.error("Debes escribir 'ELIMINAR MI CUENTA' para confirmar");
      return;
    }

    // Check if user is authenticated
    if (!session?.user || !session?.access_token) {
      toast.error("Debes estar autenticado para eliminar una cuenta");
      navigate("/auth");
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Calling delete-user-account function for authenticated user");
      
      const { data, error } = await supabase.functions.invoke('delete-user-account', {
        body: { reason },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      console.log("Function response:", { data, error });

      if (error) {
        console.error("Function error:", error);
        throw error;
      }

      if (data?.success) {
        // Show the prominent success message
        setShowSuccessMessage(true);
        
        // Wait a moment for user to see the message, then sign out and redirect
        setTimeout(async () => {
          await supabase.auth.signOut();
          navigate("/");
        }, 5000);
      } else {
        throw new Error(data?.error || "Error al eliminar la cuenta");
      }
    } catch (error) {
      console.error("Error submitting deletion request:", error);
      const errorMessage = error instanceof Error ? error.message : "Error desconocido";
      
      if (errorMessage.includes("Authentication failed")) {
        toast.error("Tu sesión ha expirado. Por favor inicia sesión nuevamente.");
        navigate("/auth");
      } else {
        toast.error("Hubo un error al procesar tu solicitud. Por favor inténtalo de nuevo.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccessMessage) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center p-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-card border-2 border-destructive/20 rounded-xl p-12 shadow-2xl">
            <div className="flex justify-center mb-6">
              <Heart className="h-16 w-16 text-destructive" />
            </div>
            <h1 className="text-4xl font-bold text-destructive mb-6">
              Tu cuenta ha sido eliminada
            </h1>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Lamentamos mucho que hayas decidido irte. Tu cuenta y todos tus datos 
                han sido eliminados completamente de nuestros sistemas.
              </p>
              <p>
                Esperamos que algún día consideres regresar. Siempre serás bienvenido/a 
                de vuelta en nuestra comunidad.
              </p>
              <p className="text-sm italic mt-8">
                Serás redirigido/a a la página principal en unos segundos...
              </p>
            </div>
            <div className="mt-8">
              <Button 
                onClick={() => navigate("/")}
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                Ir a la página principal
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <PageHeader
        title="Eliminar cuenta"
        description="Solicita la eliminación de tu cuenta y datos personales"
      />
      <div className="container max-w-2xl mx-auto p-4">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <h4 className="font-medium text-destructive mb-1">
                Esta acción es permanente e irreversible
              </h4>
              <p className="text-muted-foreground">
                Al eliminar tu cuenta, todos tus datos serán eliminados permanentemente, 
                incluyendo conversaciones guardadas, configuraciones y suscripciones activas. 
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
        </div>

        {!session?.user ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800">
              Debes estar autenticado para eliminar una cuenta. 
              <Button 
                variant="link" 
                className="p-0 h-auto text-yellow-800 underline ml-1"
                onClick={() => navigate("/auth")}
              >
                Inicia sesión aquí
              </Button>
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="reason"
                className="text-sm font-medium text-foreground"
              >
                ¿Por qué deseas eliminar tu cuenta? (opcional)
              </label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Cuéntanos el motivo por el que deseas eliminar tu cuenta..."
                className="min-h-[120px]"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="confirm"
                className="text-sm font-medium text-foreground"
              >
                Para confirmar, escribe: <span className="font-bold text-destructive">ELIMINAR MI CUENTA</span>
              </label>
              <Input
                id="confirm"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="ELIMINAR MI CUENTA"
                required
              />
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/settings")}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={isSubmitting || confirmText !== "ELIMINAR MI CUENTA"}
              >
                {isSubmitting ? "Eliminando cuenta..." : "Eliminar cuenta permanentemente"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DeleteAccount;
